import React, { useMemo, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Bot, CalendarDays, ChevronRight, CreditCard, Phone, PhoneCall, PhoneIncoming, Search, Settings, Users,
} from 'lucide-react';
import { useT } from '../../../../lib/i18n';
import { ALL_ARTICLES, HELP_CATEGORIES } from '../helpArticles';

export const ICONS = { Phone, Bot, PhoneCall, PhoneIncoming, CalendarDays, Users, CreditCard, Settings };

function Article({ article, onBack, t }) {
  return (
    <div className="hs-article">
      <button type="button" className="hs-back" onClick={onBack}><ArrowLeft size={14} />{t('Back')}</button>
      <small className="hs-crumb">{t(article.categoryTitle)}</small>
      <h3>{article.title}</h3>
      <p className="hs-summary">{article.summary}</p>
      {article.steps && <ol>{article.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
      {article.body?.map((p) => <p key={p}>{p}</p>)}
      <p className="hs-helpful">{t('Still stuck? Open a support ticket and we will help.')}</p>
    </div>
  );
}

/**
 * Help Articles: search, categories with their article counts, a category's articles, and one article.
 * `view` / `onView` let the page open a category (e.g. the step-by-step guides) from outside.
 */
export default function HelpArticles({ view, onView }) {
  const { t } = useT();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => (q ? ALL_ARTICLES.filter((a) => `${a.title} ${a.summary} ${(a.steps || []).join(' ')} ${(a.body || []).join(' ')}`.toLowerCase().includes(q)) : []), [q]);
  const article = view?.article && ALL_ARTICLES.find((a) => a.id === view.article);
  const category = view?.category && (view.category === 'guides'
    ? { key: 'guides', title: 'Step-by-step guides', articles: ALL_ARTICLES.filter((a) => a.guide) }
    : HELP_CATEGORIES.find((c) => c.key === view.category));
  const open = (a) => onView({ ...view, article: a.id });

  const list = (articles) => (
    <div className="hs-list">
      {articles.map((a) => (
        <button type="button" key={a.id} className="hs-article-row" onClick={() => open(a)}>
          <span><b>{a.title}</b><small>{a.summary}</small></span><ChevronRight size={16} />
        </button>
      ))}
    </div>
  );

  return (
    <section className="panel hs-panel hs-articles">
      <div className="hs-panel-head">
        <div><h2>{t('Help Articles')}</h2><p>{t('Find quick answers to common questions.')}</p></div>
        <button type="button" className="hs-view-all" onClick={() => { setQuery(''); onView({ category: 'all' }); }}>{t('View All')} <ArrowRight size={12} /></button>
      </div>
      {article ? <Article article={article} t={t} onBack={() => onView({ ...view, article: null })} /> : (
        <>
          <label className="hs-search"><Search size={16} /><input value={query} placeholder={t('Search help articles...')} aria-label={t('Search help articles...')}
            onChange={(e) => { setQuery(e.target.value); if (view?.category) onView(null); }} /></label>
          {q && (matches.length ? list(matches) : <p className="hs-empty">{t('No articles match "{q}". Try other words, or open a ticket.', { q: query.trim() })}</p>)}
          {!q && category && (
            <>
              <button type="button" className="hs-back" onClick={() => onView(null)}><ArrowLeft size={14} />{t('All topics')}</button>
              <h3 className="hs-cat-title">{t(category.title)}</h3>
              {list(category.articles)}
            </>
          )}
          {!q && view?.category === 'all' && (
            <>
              <button type="button" className="hs-back" onClick={() => onView(null)}><ArrowLeft size={14} />{t('All topics')}</button>
              {HELP_CATEGORIES.map((c) => <div key={c.key}><h3 className="hs-cat-title">{t(c.title)}</h3>{list(c.articles.map((a) => ({ ...a, categoryTitle: c.title })))}</div>)}
            </>
          )}
          {!q && !view?.category && (
            <div className="hs-categories">
              {HELP_CATEGORIES.map((c) => {
                const Icon = ICONS[c.icon];
                return (
                  <button type="button" key={c.key} className="hs-category" onClick={() => onView({ category: c.key })}>
                    <span className={`hs-icon ${c.tone}`}><Icon size={20} /></span>
                    <span><b>{t(c.title)}</b><small>{t(c.sub)}</small></span>
                    <em>{t('{n} articles', { n: c.articles.length })}</em><ChevronRight size={16} />
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
